// Maps to backend/routers/budget.py
import { useCallback } from 'react';
import { env } from '../utils/env';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BudgetSummary } from '../types/budget';
import { showErrorNotification } from '../utils/notificationUtils';

const etagCache = new Map<string, string>();

export interface BudgetCategory {
    id: number;
    name: string;
    allocated?: number;
    spent?: number;
    remaining?: number;
    header_id: number;
    header_name: string;
    color?: string;
    icon?: string;
    frequency_type: "week" | "month";
    frequency_value: number;
    monthly_amount: number;
    yearly_amount: number;
    is_due_this_month: boolean;
    percentage_used: number;
    iso_currency_code: string;
}

export interface BudgetHeader {
    id: number;
    name: string;
    description?: string;
    categories: BudgetCategory[];
    spending_type: string;
    monthly_total: number;
    yearly_total: number;
}

export const useBudgetCategories = (accessToken: string) => {
    const queryClient = useQueryClient();

    const {
        data: processedData,
        isLoading: loading,
        error: queryError
    } = useQuery<BudgetSummary | null, Error, { categories: BudgetCategory[], headers: BudgetHeader[] }>({
        queryKey: ['budget', 'plan'],
        enabled: !!accessToken,
        queryFn: async (): Promise<BudgetSummary | null> => {
            if (!accessToken) {
                console.warn('useBudgetCategories queryFn: No accessToken, cannot fetch.');
                return null;
            }
            try {
                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${accessToken}`,
                };
                const cachedETag = etagCache.get('budget_plan');
                if (cachedETag) {
                    headers['If-None-Match'] = cachedETag;
                }
                const response = await fetch(`${env.API_URL}/api/budget/plan`, {
                    headers,
                    credentials: 'include',
                    cache: 'no-cache',
                });
                if (response.status === 304) {
                    const cachedData = queryClient.getQueryData<BudgetSummary | null>(['budget', 'plan']);
                    return cachedData || null;
                }
                const newETag = response.headers.get('ETag');
                if (newETag) {
                    etagCache.set('budget_plan', newETag);
                }
                if (!response.ok) {
                    if (response.status !== 404) {
                        try {
                            const errorData = await response.json();
                            throw new Error(errorData.detail || 'Failed to fetch budget plan from useBudgetCategories');
                        } catch (jsonError) {
                            throw new Error('Failed to fetch budget plan from useBudgetCategories');
                        }
                    }
                    return null;
                }
                const jsonData: BudgetSummary = await response.json();
                return jsonData;
            } catch (err) {
                console.error('Error in useBudgetCategories queryFn fetching budget plan:', err);
                const errorMessage = err instanceof Error ? err.message : 'Failed to load budget data in useBudgetCategories';
                showErrorNotification(err, 'Could not load budget data (categories hook).');
                throw new Error(errorMessage);
            }
        },
        select: (data: BudgetSummary | null): { categories: BudgetCategory[], headers: BudgetHeader[] } => {
            let allCategories: BudgetCategory[] = [];
            let budgetHeaders: BudgetHeader[] = [];

            if (data && data.headers && Array.isArray(data.headers)) {
                budgetHeaders = data.headers.map((header: any) => {
                    const cats: BudgetCategory[] = Array.isArray(header.categories)
                        ? header.categories.map((cat: any) => ({
                            id: Number(cat.id),
                            name: cat.name,
                            allocated: cat.allocated,
                            spent: cat.spent,
                            remaining: cat.remaining,
                            header_id: Number(header.id),
                            header_name: header.name,
                            frequency_type: cat.frequency_type,
                            frequency_value: cat.frequency_value,
                            monthly_amount: cat.monthly_amount,
                            yearly_amount: cat.yearly_amount,
                            is_due_this_month: cat.is_due_this_month,
                            percentage_used: cat.percentage_used,
                            iso_currency_code: cat.iso_currency_code,
                            color: cat.color,
                            icon: cat.icon,
                        } as BudgetCategory))
                        : [];

                    allCategories = [...allCategories, ...cats];

                    return {
                        id: Number(header.id),
                        name: header.name,
                        description: header.description,
                        categories: cats,
                        spending_type: header.spending_type,
                        monthly_total: header.monthly_total,
                        yearly_total: header.yearly_total,
                    } as BudgetHeader;
                });

            } else {
                console.warn('useBudgetCategories select: Budget plan data is null or not in the expected format. Returning empty arrays.');
            }
            return { categories: allCategories, headers: budgetHeaders };
        },
        staleTime: 300000,
    });

    const error = queryError ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

    const refresh = useCallback(async () => {
        if (accessToken) {
            await queryClient.invalidateQueries({ queryKey: ['budget', 'plan'] });
        }
    }, [accessToken, queryClient]);

    return {
        categories: processedData?.categories || [],
        headers: processedData?.headers || [],
        loading,
        error,
        refresh
    };
}; 